/**
 * MorningMeetingReport.jsx
 * 
 * Daily morning meeting report with permission-based editing
 * - Stage <= 2 can edit Production/Shipping and Publish
 * - Each user can only edit their own attendance section
 * - Auto-save on changes
 */

import React, { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import './Morningmeetingreport.css';

const API = 'http://localhost:3001/MorningMeeting';
const getToken = () => window.localStorage.getItem('acessToken');
const getUser = () => (window.localStorage.getItem('Name') || '').replace(/[{""}]/g, '');
const getStage = () => {
  const stage = (window.localStorage.getItem('Stage') || '').replace(/[{""}]/g, '');
  return parseInt(stage) || 999; // Default to high number if not set
};

export default function MorningMeetingReport() {
  const token = getToken();
  const currentUser = getUser();
  const userStage = getStage();

  // Permissions
  const canEditProduction = userStage <= 2;
  const canPublish = userStage <= 2;
  const canEditShipping = userStage <= 2;

  // Today's date (for display) and yesterday's date (for data)
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [reportDate] = useState(todayStr);
  const [status, setStatus] = useState('Draft');
  const [lastSaved, setLastSaved] = useState(null);

  // Auto-save timer ref
  const autoSaveTimer = useRef(null);

  // Production data
  const [productionData, setProductionData] = useState([
    { part_type: 'Rigid', production_vs_target: '', reason: '' },
    { part_type: 'Flex', production_vs_target: '', reason: '' }
  ]);

  // Shipping data
  const [shippingData, setShippingData] = useState({
    qty_shipped_yesterday: '',
    qty_to_be_shipped: 0,
    po_reference: '',
    reason: '',
    partial_shipments: [],
    not_shipped: []
  });

  // Attendance data - using actual employee names from database
  const [attendanceData, setAttendanceData] = useState([
    { employee_name: 'Sahil Sahil', emp_id: 18, status: 'Not Recorded', comments: '', position: 'Production Supervisor', login_name: 'Sahil' },
    { employee_name: 'KENIL SHAH', emp_id: 71, status: 'Not Recorded', comments: '', position: 'Manufacturing Manager', login_name: 'Kenil Shah' },
    { employee_name: 'Adrian Sin', emp_id: 43, status: 'Not Recorded', comments: '', position: '', login_name: 'Adrian Sin' },
    { employee_name: 'Gowrigan Inparadnam (Rigan)', emp_id: 38, status: 'Not Recorded', comments: '', position: '', login_name: 'Rigan Inpam' },
    { employee_name: 'Julie Ragazzo', emp_id: 36, status: 'Not Recorded', comments: '', position: '', login_name: 'Julie' },
    { employee_name: 'Victor Gonsalves', emp_id: 39, status: 'Not Recorded', comments: '', position: '', login_name: 'Victor Goncalves' }
  ]);
  const [attendanceRecorded, setAttendanceRecorded] = useState(false);

  // Feedback data
  const [feedbackData, setFeedbackData] = useState([
    { person: 'Patel Feedback', feedback: '', can_edit: false },
    { person: 'Paolo Feedback', feedback: '', can_edit: false }
  ]);

  // Determine which sections current user can edit
  const canEditAttendance = (employeeLoginName) => {
    return currentUser === employeeLoginName;
  };

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // First, try to load existing report for today
      const existingReport = await Axios.get(`${API}/report`, {
        params: { token, date: reportDate }
      });

      if (existingReport.data.success && existingReport.data.data) {
        // Load saved report
        const report = existingReport.data.data;
        setProductionData(report.production_data);
        setShippingData(report.shipping_data);
        setAttendanceData(report.attendance_data);
        setFeedbackData(report.feedback_data);
        setStatus(report.status);
        setLastSaved(new Date(report.updated_at));
      } else {
        // No existing report - load yesterday's data to auto-populate
        await loadYesterdayData();
      }
    } catch (error) {
      console.error('Error loading report:', error);
      // If no report exists, load yesterday's data
      await loadYesterdayData();
    } finally {
      setLoading(false);
    }
  };

  const loadYesterdayData = async () => {
    try {
      // Load production data
      const prodRes = await Axios.get(`${API}/yesterday-production`, { params: { token } });
      console.log('Production data received:', prodRes.data);

      if (prodRes.data) {
        const rigidData = prodRes.data.rigid;
        const flexData = prodRes.data.flex;

        const rigidPct = rigidData.target > 0
          ? `${Math.round((rigidData.produced / rigidData.target) * 100)}%`
          : '0%';

        const flexPct = flexData.target > 0
          ? `${Math.round((flexData.produced / flexData.target) * 100)}%`
          : '0%';

        setProductionData([
          {
            part_type: 'Rigid',
            production_vs_target: `${rigidData.produced}/${rigidData.target} (${rigidPct})`,
            reason: '',
            target: rigidData.target,
            produced: rigidData.produced,
            parts_count: rigidData.parts_count
          },
          {
            part_type: 'Flex',
            production_vs_target: `${flexData.produced}/${flexData.target} (${flexPct})`,
            reason: '',
            target: flexData.target,
            produced: flexData.produced,
            parts_count: flexData.parts_count
          }
        ]);
      }

      // Load shipping data
      const shipRes = await Axios.get(`${API}/yesterday-shipping`, { params: { token } });
      console.log('Shipping data received:', shipRes.data);

      if (shipRes.data) {
        setShippingData({
          qty_shipped_yesterday: shipRes.data.total_qty_shipped || 0,
          qty_to_be_shipped: shipRes.data.total_qty_to_ship || 0,
          po_reference: shipRes.data.po_reference || '',
          reason: '',
          partial_shipments: shipRes.data.partial_shipments || [],
          not_shipped: shipRes.data.not_shipped || [],
          issues_count: shipRes.data.issues_count || 0
        });
      }

      // Load TODAY's attendance
      const attRes = await Axios.get(`${API}/today-attendance`, { params: { token } });
      console.log('Attendance data received:', attRes.data);

      if (attRes.data) {
        if (attRes.data.has_data && attRes.data.employees && attRes.data.employees.length > 0) {
          setAttendanceRecorded(true);
          const employeeMap = {};
          attRes.data.employees.forEach(emp => {
            employeeMap[emp.Employee_Name] = {
              status: emp.Status,
              position: emp.Position
            };
          });

          setAttendanceData(prev => prev.map(emp => ({
            ...emp,
            status: employeeMap[emp.employee_name]?.status || 'Not Recorded',
            position: employeeMap[emp.employee_name]?.position || emp.position,
            comments: ''
          })));
        } else {
          setAttendanceRecorded(false);
        }
      }
    } catch (error) {
      console.error('Error loading yesterday data:', error);
    }
  };

  // Auto-save function
  const triggerAutoSave = () => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(() => {
      handleAutoSave();
    }, 2000); // Auto-save 2 seconds after user stops typing
  };

  const handleAutoSave = async () => {
    if (status === 'Published') return; // Don't auto-save if published

    setAutoSaving(true);
    try {
      const response = await Axios.post(
        `${API}/save?token=${token}`,
        {
          meeting_date: reportDate,
          production_data: productionData,
          shipping_data: shippingData,
          attendance_data: attendanceData,
          feedback_data: feedbackData,
          status: 'Draft'
        }
      );

      if (response.data.success) {
        setLastSaved(new Date());
        console.log('Auto-saved successfully');
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await Axios.post(
        `${API}/save?token=${token}`,
        {
          meeting_date: reportDate,
          production_data: productionData,
          shipping_data: shippingData,
          attendance_data: attendanceData,
          feedback_data: feedbackData,
          status: status
        }
      );

      if (response.data.success) {
        setLastSaved(new Date());
        alert('Report saved successfully!');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Are you sure you want to publish this report? Published reports are final.')) {
      return;
    }

    setSaving(true);
    try {
      await handleSave();
      const response = await Axios.post(
        `${API}/publish?token=${token}`,
        { meeting_date: reportDate }
      );

      if (response.data.success) {
        setStatus('Published');
        alert('Report published successfully!');
      }
    } catch (error) {
      console.error('Publish error:', error);
      alert('Error publishing report');
    } finally {
      setSaving(false);
    }
  };

  const updateProduction = (index, field, value) => {
    const updated = [...productionData];
    updated[index][field] = value;
    setProductionData(updated);
    triggerAutoSave();
  };

  const updateAttendance = (index, field, value) => {
    const updated = [...attendanceData];
    updated[index][field] = value;
    setAttendanceData(updated);
    triggerAutoSave();
  };

  const updateFeedback = (index, field, value) => {
    const updated = [...feedbackData];
    updated[index][field] = value;
    setFeedbackData(updated);
    triggerAutoSave();
  };

  if (loading) {
    return (
      <div className="mmr">
        <div className="mmr__loading">
          <div className="mmr__spinner" />
          <div>Loading meeting data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mmr">
      {/* Header */}
      <div className="mmr__header">
        <div className="mmr__header-left">
          <div className="mmr__date-badge">
            <div className="mmr__date-label">Meeting Date</div>
            <div className="mmr__date-value">{today.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</div>
          </div>
          <div className="mmr__data-source">
            <span className="mmr__source-label">Data from:</span>
            <span className="mmr__source-date">{yesterday}</span>
          </div>
        </div>
        <div className="mmr__header-right">
          {autoSaving && <div className="mmr__autosave-indicator">💾 Saving...</div>}
          {lastSaved && !autoSaving && (
            <div className="mmr__last-saved">
              Last saved: {lastSaved.toLocaleTimeString()}
            </div>
          )}
          <div className={`mmr__status-badge mmr__status-badge--${status.toLowerCase()}`}>
            {status === 'Published' ? '✓ Published' : '○ Draft'}
          </div>
          <div className="mmr__user">
            👤 {currentUser}
            {userStage <= 2 && <span className="mmr__admin-badge">Manager</span>}
          </div>
        </div>
      </div>

      <div className="mmr__container">
        <div className="mmr__title-bar">
          <h1 className="mmr__title">Morning Meeting Report</h1>
          <div className="mmr__subtitle">Yesterday's Performance Review</div>
        </div>

        {/* Permissions info */}
        {!canEditProduction && (
          <div className="mmr__info-banner">
            ℹ️ You have view-only access to Production and Shipping sections. You can edit your attendance and feedback only.
          </div>
        )}

        {/* Section 1: Production Performance */}
        <div className="mmr__section">
          <div className="mmr__section-header">
            <div className="mmr__section-icon">📊</div>
            <h2 className="mmr__section-title">Yesterday's Performance</h2>
            {!canEditProduction && <span className="mmr__lock-badge">🔒 View Only</span>}
          </div>

          <table className="mmr__table">
            <thead>
              <tr>
                <th>Part Type</th>
                <th>Production vs Target</th>
                <th>Reason (if below target)</th>
              </tr>
            </thead>
            <tbody>
              {productionData.map((item, idx) => (
                <tr key={idx}>
                  <td className="mmr__cell-strong">{item.part_type}</td>
                  <td>
                    <div className="mmr__prod-display">
                      {item.production_vs_target || 'Loading...'}
                    </div>
                  </td>
                  <td>
                    <textarea
                      className="mmr__textarea mmr__textarea--reason"
                      value={item.reason}
                      onChange={(e) => updateProduction(idx, 'reason', e.target.value)}
                      placeholder="Enter reason if needed"
                      disabled={status === 'Published' || !canEditProduction}
                      rows={3}
                    />
                  </td>
                </tr>
              ))}
              <tr>
                <td className="mmr__cell-strong">Shipping</td>
                <td>
                  <div className="mmr__shipping-input">
                    <div className="mmr__shipping-display">
                      {shippingData.qty_shipped_yesterday || 0}
                    </div>
                    <span className="mmr__shipping-divider">/</span>
                    <span className="mmr__shipping-target">{shippingData.qty_to_be_shipped || 0}</span>
                  </div>
                  <div className="mmr__shipping-meta">
                    {shippingData.po_reference && (
                      <span className="mmr__po-ref">PO: {shippingData.po_reference}</span>
                    )}
                    {shippingData.issues_count > 0 && (
                      <span className="mmr__shipping-issues">
                        ⚠ {shippingData.issues_count} PO(s) with issues
                      </span>
                    )}
                  </div>
                  {/* Show partial/not shipped POs */}
                  {(shippingData.partial_shipments.length > 0 || shippingData.not_shipped.length > 0) && (
                    <div className="mmr__shipping-details">
                      {shippingData.not_shipped.map((po, i) => (
                        <div key={i} className="mmr__shipping-issue mmr__shipping-issue--not-shipped">
                          <strong>{po.po_no}</strong>: Not shipped ({po.expected} units)
                        </div>
                      ))}
                      {shippingData.partial_shipments.map((po, i) => (
                        <div key={i} className="mmr__shipping-issue mmr__shipping-issue--partial">
                          <strong>{po.po_no}</strong>: Partial - {po.shipped}/{po.expected} (missing {po.missing})
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  <textarea
                    className="mmr__textarea mmr__textarea--reason"
                    value={shippingData.reason}
                    onChange={(e) => { setShippingData({ ...shippingData, reason: e.target.value }); triggerAutoSave(); }}
                    placeholder="Reason if qty mismatch"
                    disabled={status === 'Published' || !canEditShipping}
                    rows={3}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Section 2: Attendance */}
        <div className="mmr__section">
          <div className="mmr__section-header">
            <div className="mmr__section-icon">👥</div>
            <h2 className="mmr__section-title">Attendance Dropdown</h2>
            {!attendanceRecorded && (
              <span className="mmr__warning-badge">⚠ Attendance Not Recorded Yet</span>
            )}
          </div>

          <table className="mmr__table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Position</th>
                <th>Status</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              {attendanceData.map((emp, idx) => {
                const canEdit = canEditAttendance(emp.login_name);
                return (
                  <tr key={idx} className={emp.status === 'Not Recorded' ? 'mmr__row-not-recorded' : ''}>
                    <td className="mmr__cell-strong">
                      {emp.employee_name}
                      {canEdit && <span className="mmr__edit-badge">✏️ Your section</span>}
                    </td>
                    <td className="mmr__cell-position">{emp.position || '—'}</td>
                    <td>
                      <select
                        className="mmr__select"
                        value={emp.status}
                        onChange={(e) => updateAttendance(idx, 'status', e.target.value)}
                        disabled={status === 'Published' || !canEdit}
                      >
                        <option value="Not Recorded">Not Recorded</option>
                        <option value="Present">Present</option>
                        <option value="Absent">Absent</option>
                        <option value="Late">Late</option>
                        <option value="Sick">Sick</option>
                        <option value="Vacation">Vacation</option>
                      </select>
                    </td>
                    <td>
                      <textarea
                        className="mmr__textarea"
                        value={emp.comments}
                        onChange={(e) => updateAttendance(idx, 'comments', e.target.value)}
                        placeholder={canEdit ? "Add notes" : "View only"}
                        rows={3}
                        disabled={status === 'Published' || !canEdit}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Section 3: Feedback */}
        <div className="mmr__section">
          <div className="mmr__section-header">
            <div className="mmr__section-icon">💬</div>
            <h2 className="mmr__section-title">Team Feedback</h2>
          </div>

          <table className="mmr__table">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Person</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {feedbackData.map((item, idx) => {
                // Only managers (Stage <= 2) can edit feedback
                const canEditFeedback = userStage <= 2;
                return (
                  <tr key={idx}>
                    <td className="mmr__cell-strong">
                      {item.person}
                      {!canEditFeedback && <span className="mmr__lock-badge">🔒</span>}
                    </td>
                    <td>
                      <textarea
                        className="mmr__textarea"
                        value={item.feedback}
                        onChange={(e) => updateFeedback(idx, 'feedback', e.target.value)}
                        placeholder={canEditFeedback ? "Enter feedback..." : "Manager access only"}
                        rows={4}
                        disabled={status === 'Published' || !canEditFeedback}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action buttons */}
        <div className="mmr__actions">
          <button
            className="mmr__btn mmr__btn--save"
            onClick={handleSave}
            disabled={saving || status === 'Published'}
          >
            {saving ? 'Saving...' : '💾 Save Draft'}
          </button>
          {canPublish && status !== 'Published' && (
            <button
              className="mmr__btn mmr__btn--publish"
              onClick={handlePublish}
              disabled={saving}
            >
              ✓ Publish Report
            </button>
          )}
          {!canPublish && (
            <div className="mmr__publish-info">
              ℹ️ Only managers can publish reports
            </div>
          )}
          <button
            className="mmr__btn mmr__btn--ghost"
            onClick={() => window.location.href = '/morning-meeting-activity-log'}
          >
            📋 View Activity Log
          </button>
        </div>
      </div>
    </div>
  );
}